/**
 * Capabilities:
 * - CAP-MEMBER-ONBOARDING
 * - CAP-MEMBER-SELF-SERVICE
 * - CAP-VOUCHER-REDEMPTION
 * - CAP-PROMOTION-PARTICIPATION
 *
 * Responsibilities:
 * - Host the browser shell for the member-facing Telegram web app.
 * - Resolve the launch token from URL or local storage and bootstrap the local
 *   member snapshot used by downstream Telegram components.
 * - Fall back to a guarded loading/error experience on mobile clients.
 *
 * Context:
 * - project-intelligence/architecture/frontend-applications.md
 * - project-intelligence/generated/frontend-routes.md
 * - project-intelligence/capabilities/requested/member/member-onboarding.md
 * - project-intelligence/capabilities/requested/member/member-self-service.md
 * - project-intelligence/capabilities/requested/member/voucher-redemption.md
 * - project-intelligence/capabilities/requested/member/promotion-participation.md
 */

/* eslint-disable no-mixed-operators */

import React, { useState, useEffect, useMemo } from 'react';
import { isMobile } from 'react-device-detect';
import { useNavigate, useLocation } from "react-router-dom";

import Button from 'react-bootstrap/Button';

import { isDev } from '../../utils/general-utils';
import { useSimpleQuery } from '../../hooks';

import { FullWidthSpinner } from '../loader-components';

import { getMemberDataWithToken, clearMemberDataInLocalStorage, removeOldVoucherDataInLocalStorage } from './utils';
import { HorizontallyCenteringContainer, VerticalSeparator } from './utils';
import { DEFAULT_BACKGROUND_COLOR } from './utils';
// import { MEMBERSHIP_TIERS } from './MembershipCard';

import { TelegramAppForMobileConfigured } from './TelegramAppForMobileConfigured';
import { TelegramAppForMobileUnconfigured } from './TelegramAppForMobileUnconfigured';

import './telegram-app.css';

const HIDE_CLEAR_CONFIG_BUTTON = true;

class ErrorBoundary extends React.Component {
    constructor(props) {
      super(props);
      this.state = { hasError: false };
    }
  
    static getDerivedStateFromError(error) {
      // Update state so the next render will show the fallback UI.
      return { hasError: true };
    }
  
    // componentDidCatch(error, errorInfo) {
    //   // You can also log the error to an error reporting service
    //   logErrorToMyService(error, errorInfo);
    // }
  
    render() {
      if (!isDev && this.state.hasError) {
        // You can render any custom fallback UI
        return (
            <FullWidthSpinner
                text='Sorry, something has gone wrong...'
                heightInVH={isMobile ? 74 : 100}
                scale={3} />
        )
      }
  
      return this.props.children; 
    }
  }

export function TelegramApp() {
    /**
     * Member-app bootstrapper.
     *
     * The Telegram entry flow is token-centric: this component normalizes the
     * token source, refreshes member data when possible, and then chooses
     * between configured and unconfigured app shells.
     */
    const [token, setToken] = useState(null);
    const [memberData, setMemberData] = useState(null);

    const query = useSimpleQuery();
    const location = useLocation();
    const navigate = useNavigate();

    const tokenFromQueryParams = query.token || '';
    const tokenFromQueryParamsSplit = tokenFromQueryParams.split(':');
    const validTokenFromQueryParams = tokenFromQueryParamsSplit.length === 3;

    // Set title
    useEffect(() => {
        document.getElementsByTagName('title')[0].innerText = 'Swensen\'s Cool Rewards';
    }, []);

    // Remove old voucher redemption data
    useEffect(() => {
        removeOldVoucherDataInLocalStorage();
    }, []);

    // Redirect after grabbing token from URL (if available)
    useEffect(() => {
        if (validTokenFromQueryParams) {
            localStorage.setItem('token', tokenFromQueryParams);
            getMemberDataWithToken(tokenFromQueryParams, setMemberData)
                .then(() => { })
                .catch(() => { })
                .then(() => {
                    navigate(location.pathname);
                });
        }
    }, [location.pathname, navigate, query, validTokenFromQueryParams, tokenFromQueryParams]);

    const _token = localStorage.getItem('token');
    // updateMemberData = _token && (() => getMemberDataWithToken(_token, setMemberData));
    const updateMemberData = useMemo(() => _token && (() => getMemberDataWithToken(_token, setMemberData)), [_token])

    // Grab token from LocalStorage and configure App
    useEffect(() => {
        if (!validTokenFromQueryParams) {
            const _memberData = JSON.parse(localStorage.getItem('lastMemberRecord') || null);
            if (!!_memberData) {
                setMemberData(_memberData);
            }
            if (isDev) {
                window._app__memberData = _memberData;
            }
            const _token = localStorage.getItem('token');
            if (!!_token) {
                setToken(_token);
                getMemberDataWithToken(_token, setMemberData)
                    .catch(e => {
                        console.error('Unable to retrieve updated member data:', e);
                        localStorage.removeItem('lastMemberRecord');
                        setMemberData(null);
                    });
            }
        }
    }, [validTokenFromQueryParams]);

    // This has to be after all the effects
    if (validTokenFromQueryParams) {
        return (
            <FullWidthSpinner
                text='Setting things up...'
                heightInVH={isMobile ? 74 : 100}
                scale={3} />
        );
    }

    function clearAppSecret() {
        clearMemberDataInLocalStorage();
        setToken(null);
        setMemberData(null);
    }

    return (
        <ErrorBoundary>
            <div>
                <HorizontallyCenteringContainer>
                    <div style={{
                        position:'absolute', top: '0px', zIndex: '-1',
                        width: '100%',
                        height: 'calc(20px)',
                        // height: 'calc(20px + 36px + 20px)',
                        backgroundColor: 'var(--app-top-strip-color)'
                        // backgroundColor: 'var(--swensens-brand-color)'
                    }}/>
                    <div style={{
                        position:'absolute', top: '0px', zIndex: '-2',
                        width: '100%', height: '100vh',
                        backgroundColor: DEFAULT_BACKGROUND_COLOR,
                    }}/>
                    {
                        !token ? (
                            <TelegramAppForMobileUnconfigured />
                        ) : (
                            <TelegramAppForMobileConfigured clearAppSecret={clearAppSecret} token={token} memberData={memberData} updateMemberData={updateMemberData}/>
                        )
                    }
                </HorizontallyCenteringContainer>
                {
                    !isDev && (
                        <VerticalSeparator height='2em'/>
                    )
                }
                {
                    !HIDE_CLEAR_CONFIG_BUTTON && isDev && !!token && !!memberData && (
                        <>
                            <VerticalSeparator/>
                            <HorizontallyCenteringContainer padding='10px'>
                                <Button variant="secondary" onClick={clearAppSecret}>Clear Configuration</Button>
                            </HorizontallyCenteringContainer>
                            <VerticalSeparator/>
                        </>
                    )
                }
            </div>
        </ErrorBoundary>
    );
}
